import { selectLocationPathname, selectLocationSearch, useLocationSelector } from '@shared/core/router.helper/useLocationSelector';
import { useCardsRouterContext } from '@apps/card/Card.routes';
// Direct, not via the `@shared/utils/search` barrel: that would pull the scorer and the curated
// results table into every consumer of the gate, the server's own bundle included.
import { useInitialThemeSearchIndex } from '@shared/utils/search/ThemeSearchIndexProvider';
import { useHasStationeryProducts } from '@apps/card/hooks/useProducts';
import { isServerRenderedGalleryUrl } from './galleryParams';

/**
 * True on a URL the server rendered the gallery for: the public shop's
 * `/cards/wedding/:category`, bare or carrying one of the crawlable filter shapes in
 * `galleryParams`. Mirrors the server gate in `publicGallerySsrGate.ts`; the two must agree, or
 * the client reproduces a different first render from the server's markup.
 *
 * For an index-backed URL (`style:*`) that agreement rests on the inlined index: the server
 * inlines it only when it rendered with it, so its presence here answers the same question the
 * server answered with its own fetch. A products-backed URL (`format:*`, `product:magnet`) works
 * the same way through the inlined Apollo cache, which the server seeds only when its prefetch
 * succeeded.
 */
export const useIsPublicGallerySsr = (): boolean => {
  const { isPublicShopRoute } = useCardsRouterContext();
  const pathname = useLocationSelector(selectLocationPathname);
  const search = useLocationSelector(selectLocationSearch);
  const hasThemeSearchIndex = !!useInitialThemeSearchIndex();
  const hasProducts = useHasStationeryProducts();
  return isPublicShopRoute && isServerRenderedGalleryUrl(pathname, search, { hasThemeSearchIndex, hasProducts });
};
